import type { ReactNode } from 'react';

/**
 * A titled content area inside a Customer Hub tab.
 *
 * Every tab that shows a list uses this so the three states the brief demands
 * (loading / empty / error) always look and behave the same, and — more
 * importantly — so a *failed* request can never be rendered as "0". `count`
 * is only shown when the caller really got a successful response back.
 *
 * The panel itself has no surface: its title sits on the page background and
 * the content below is the single white layer (HubSurface / Kpi), so a tab
 * never stacks a box inside a box.
 */
export function HubPanel({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 min-w-0">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-surface-900">{title}</h2>
          {subtitle && <p className="text-xs text-surface-600 mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
        {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
      </div>
      {children}
    </section>
  );
}

/**
 * The white surface inside a hub tab. `flush` (default) lets a table or a
 * list run edge to edge; pass `padded` for text/forms.
 */
export function HubSurface({
  children,
  padded = false,
  className = '',
}: {
  children: ReactNode;
  padded?: boolean;
  className?: string;
}) {
  return (
    <div className={`bg-surface-0 border border-surface-200 rounded-xl min-w-0 ${padded ? 'p-4 sm:p-5' : 'overflow-hidden'} ${className}`}>
      {children}
    </div>
  );
}

/** Scroll container for a table inside HubSurface (only the table scrolls, never the page). */
export function HubTable({ children }: { children: ReactNode }) {
  return <div className="overflow-x-auto">{children}</div>;
}
